import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { consumePostAuthRedirect } from '../lib/postAuthRedirect';
import { useViewMode } from '../contexts/ViewModeContext';
import { CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION } from '../config/consent';
import { getAffiliateCode } from '../lib/affiliate';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { setViewMode } = useViewMode();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // PKCE flow: email confirmation links include a ?code= parameter
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            // Code may have already been exchanged by AuthContext - not fatal
            console.warn('Code exchange failed (may already be consumed):', exchangeError.message);
          }
        }

        // Small delay to let AuthContext finish if it raced us
        await new Promise(r => setTimeout(r, 500));
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Auth callback error:', sessionError);
          setError(sessionError.message);
          return;
        }

        if (session) {
          // Check if this was a recruiter signup
          const isRecruiterSignup = sessionStorage.getItem('google_signup_recruiter') === 'true';
          sessionStorage.removeItem('google_signup_recruiter');

          // Check if consent already recorded (existing user)
          const existingConsent = session.user?.user_metadata?.terms_accepted_at;

          // Record consent for new users OR update recruiter status
          if (!existingConsent || isRecruiterSignup) {
            const updateData: Record<string, any> = {
              terms_accepted_at: new Date().toISOString(),
              terms_version: CURRENT_TERMS_VERSION,
              privacy_accepted_at: new Date().toISOString(),
              privacy_version: CURRENT_PRIVACY_VERSION
            };
            
            if (isRecruiterSignup) {
              updateData.is_recruiter = true;
            } else if (!existingConsent) {
              // Explorer OAuth signup (new user): attach partner affiliate code
              // (first-touch) if the user arrived via a partner link. Internal-only;
              // re-validated + persisted server-side. Existing users are skipped.
              const affiliateCode = getAffiliateCode();
              if (affiliateCode) {
                updateData.affiliate_code = affiliateCode;
                updateData.affiliate_captured_at = new Date().toISOString();
              }
            }

            const { error: updateError } = await supabase.auth.updateUser({
              data: updateData
            });
            
            if (updateError) {
              console.error('Error updating user metadata:', updateError);
            }
          }

          // Redirect based on user type
          const isRecruiter = session.user?.user_metadata?.is_recruiter || isRecruiterSignup;

          // Reset the shell view-mode to the account's natural experience.
          setViewMode(isRecruiter ? 'recruiter' : 'explorer');

          // Redirect priority:
          //   1. postAuthRedirect (set e.g. by invite landing — survives
          //      the email-confirmation round-trip on signup)
          //   2. legacy google_return_url for OAuth landings
          //   3. role default
          const pendingRedirect = consumePostAuthRedirect();
          const legacyReturn = sessionStorage.getItem('google_return_url');
          sessionStorage.removeItem('google_return_url');

          const target =
            (pendingRedirect && pendingRedirect.startsWith('/') ? pendingRedirect : null)
            || (legacyReturn && legacyReturn !== '/' ? legacyReturn : null);

          if (target) {
            navigate(target, { replace: true });
          } else if (isRecruiter) {
            navigate('/?tab=hire', { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          // No session, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Authentication failed. Please try again.');
      }
    };

    handleAuthCallback();
  }, [navigate, setViewMode]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-xs">
        <div className="bg-white rounded-xl border border-slate-200 p-6 relative overflow-hidden">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-pink-50/80 to-transparent z-10" />
          <div className="space-y-3 mb-4">
            <div className="h-4 w-2/3 mx-auto bg-pink-100 rounded-full animate-pulse" />
            <div className="h-3 w-full bg-slate-100 rounded-full animate-pulse" />
            <div className="h-3 w-4/5 mx-auto bg-slate-100 rounded-full animate-pulse" />
          </div>
          <p className="text-xs text-center text-slate-400">Completing sign in...</p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
